<template>
    <section ref="section" class="hero py-16 lg:py-24 px-8"
        :class="{ anti: anti, 'text-center': !left, 'sm:px-[15%]': !left, 'px-3': left, 'sm:px-28': left }">
        <div class="w-full"
            :class="{ 'fade-in-section': isFadeIn, flex: !!(image_url || sidebar), 'clear': isInvisible }">
            <div
                :class="{ 'sm:w-4/5': (sidebar || image_url) && !image_96 && !image_128, 'sm:w-3/5': (sidebar || image_url) && (image_96 || image_128) }">
                <div class="body-hero items-center justify-center">
                    <h4 v-if="pretitle" class="text-xl justify-center font-semibold mb-4">
                        {{ pretitle }}
                    </h4>
                    <component :is="headingLevel" class="text-title gap-2 sm:gap-x-4 flex flex-wrap"
                        :class="{ 'justify-center': !left }">
                        <span v-for="(word, index) in stylizedTitle" :key="index" :class="{ 'colour': word.highlight }">
                            {{ word.text }}
                        </span>
                    </component>
                    <div class="w-full flex" :class="{ 'justify-center': !left }">
                        <p class="mt-4 sm:w-3/4 text-subtitle">
                            {{ subtitle }}

                            <slot></slot>
                        </p>
                    </div>

                </div>
                <div class="parent-arrow-icon mt-8 sm:flex sm:gap-6" :class="{ 'justify-center': !left }"
                    v-if="buttons?.length">
                    <Button v-for="(btn, index) of buttons" :key="index" :href="btn.link" :title="btn.title"
                        :icon="btn.icon" :variant="index === 0 && btn.title ? 'primary' : 'secondary'"
                        :posthog-click-event="btn.posthog_click_event"
                        :posthog-click-properties="btn.posthog_click_properties" :center="!left" />
                </div>
            </div>
            <div class="justify-right" :class="!image_96 && !image_128 ? 'sm:w-1/5 mt-8' : 'sm:w-2/5'"
                v-if="image_url || sidebar">
                <img v-if="image_url" :src="image_url"
                    class="object-contain float-right rounded-lg bg-[var(--white)] px-4 py-1 hidden lg:inline-block"
                    :class="image_96 ? 'w-96' : image_128 ? 'w-128' : 'w-32 h-32'" />

                <slot name="sidebar" v-if="sidebar"></slot>
            </div>
        </div>
        <div class="w-full flex items-center justify-center mt-20" v-if="bottom_image_url">
            <img :src="bottom_image_url" />
        </div>

        <!-- Hero Footer -->
        <div class="flex flex-col sm:flex-row gap-8 text-neutral-200 mt-8" v-if="footerItems?.length">
            <p v-for="(item, index) in footerItems" :key="index" class="flex items-center gap-2">
                <Icon name="mdi:check" class="text-blue-500 text-lg" />{{ item }}
            </p>
        </div>
    </section>
</template>

<script lang="ts">
import posthog from 'posthog-js';
import { type PropType } from 'vue';
import { isDev } from '~/services/common';
import Button from '~/components/Button.vue';

export interface IHeroButton {
    link: string;
    title?: string;
    icon?: string;
    posthog_click_event?: string;
    posthog_click_properties?: any;
}

export default {
    props: {
        title: {
            type: String,
            required: true,
        },
        highlightWords: {
            type: Array as () => string[],
            default: () => [],
        },
        pretitle: {
            type: String,
            default: '',
        },
        image_url: String,
        image_96: Boolean,
        image_128: Boolean,
        colourIndex: String, // TODO: This is an extremely brittle and awkward implementation that binds hard-coded content to UI logic
        subtitle: String,
        subsubtitle: String,
        buttons: Array as PropType<IHeroButton[]>,
        anti: Boolean,
        left: Boolean,
        bottom_image_url: String,
        sidebar: Boolean,
        posthog_view_event: String,
        posthog_view_properties: Object,
        isInvisible: {
            type: Boolean,
            default: false,
        },
        footerItems: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        headingLevel: {
            type: String as PropType<'h1' | 'h2'>,
            default: 'h1',
        },
    },
    data() {
        return {
            title_array: this.title.split(' '),
            colour_indexes: this.colourIndex?.split(',') || [],
            isFadeIn: false,
        };
    },
    computed: {
        stylizedTitle() {
            const words = this.title.split(' ');

            // For backwards compatibility
            if (this.colourIndex) {
                return words.map((word, index) => ({
                    text: word,
                    highlight: this.colour_indexes.includes(String(index))
                }))
            }

            return words.map(word => {
                const lowerWord = word.toLowerCase();

                return {
                    text: word,
                    highlight: this.highlightWords.some(phrase =>
                        phrase.toLowerCase().split(' ').includes(lowerWord)
                    )
                };
            });
        }
    },
    methods: {
        click(posthog_click_event?: string, posthog_click_properties?: any) {
            if (isDev()) {
                return;
            }

            if (posthog_click_event) {
                posthog.capture(posthog_click_event, posthog_click_properties);
            }
        },
        processTitle(title: string) {
            const words = title.split(' ');
            return words.map((word, index) => {
                if (this.colour_indexes.includes(String(index))) {
                    return `<span class="colour">${word}</span>`;
                }
                return word;
            }).join(' ');
        },

    },
    mounted() {
        if (this.posthog_view_event) {
            posthog.capture(this.posthog_view_event, this.posthog_view_properties);
        }

        // Use nextTick to ensure the DOM has updated
        this.$nextTick(() => {
            setTimeout(() => {
                this.isFadeIn = true
            }, 10)
        })
    },
    components: {
        Button
    },
};
</script>

<style scoped>
.clear {
    opacity: 0;
    visibility: hidden;
    transition: opacity 250ms ease-out;
    will-change: opacity, visibility;
}

.fade-in-section {
    opacity: 1;
    visibility: visible;
}
</style>